import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { sellerService } from 'api/services/SellerService';
import type {
  SellerProfileViewModel,
  SellerRatingViewModel,
  SellerListingCardViewModel,
} from 'api/types/seller';
import type { PaginatedList } from 'api/types/common/PaginatedList';
import AdListGridItem from 'components/AdList/AdListGridItem';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';
import { sellerListingCardToAdListing } from 'api/utils/listingMapper';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RATINGS_PAGE_SIZE = 10;
const LISTINGS_PAGE_SIZE = 12;

const PLACEHOLDER_COMPANY_LOGO =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%230d6efd"%3E%3Cpath d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/%3E%3C/svg%3E';
const formatDateTr = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR');
};

const locationLabel = (city?: string | null, district?: string | null): string => {
  const parts = [district, city].filter((p): p is string => !!p && p.trim() !== '');
  return parts.join(', ');
};

const SaticiProfilKurumsal: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const profileUserId = id ?? '';
  const isGuidProfile = GUID_RE.test(profileUserId);

  const [profile, setProfile] = useState<SellerProfileViewModel | null | undefined>(undefined);
  const [ratings, setRatings] = useState<PaginatedList<SellerRatingViewModel> | null>(null);
  const [listings, setListings] = useState<PaginatedList<SellerListingCardViewModel> | null>(null);
  const [ratingsPage, setRatingsPage] = useState(1);
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isGuidProfile) {
      setProfile(null);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const response = await sellerService.getProfile(profileUserId);
      if (cancelled) return;
      setProfile(response.isSuccess ? response.data : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [profileUserId, isGuidProfile]);

  const loadRatings = useCallback(
    async (page: number) => {
      if (!isGuidProfile) return;
      setRatingsLoading(true);
      try {
        const response = await sellerService.getRatings(
          profileUserId,
          page,
          RATINGS_PAGE_SIZE,
        );
        if (response.isSuccess && response.data) setRatings(response.data);
      } finally {
        setRatingsLoading(false);
      }
    },
    [profileUserId, isGuidProfile],
  );

  const loadListings = useCallback(
    async (page: number) => {
      if (!isGuidProfile) return;
      setListingsLoading(true);
      try {
        const response = await sellerService.getListings(
          profileUserId,
          page,
          LISTINGS_PAGE_SIZE,
        );
        if (response.isSuccess && response.data) setListings(response.data);
      } finally {
        setListingsLoading(false);
      }
    },
    [profileUserId, isGuidProfile],
  );

  useEffect(() => {
    if (profile) loadRatings(ratingsPage);
  }, [profile, ratingsPage, loadRatings]);

  useEffect(() => {
    if (profile) loadListings(listingsPage);
  }, [profile, listingsPage, loadListings]);

  const ratingAverageLabel = useMemo(() => {
    if (!profile) return '0.0';
    return profile.ratingAverage.toFixed(1);
  }, [profile]);

  const handleSendMessage = () => navigate('/mesajlar');

  if (profile === undefined) {
    return <div className="container py-5 text-center">Satıcı bilgileri yükleniyor...</div>;
  }

  if (profile === null) {
    return <div className="container py-5 text-center">Satıcı bulunamadı</div>;
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="container py-4">
        {/* Satıcı Profil Kartı */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-2 text-center">
                <img
                  alt="Şirket Logo"
                  className="rounded-circle mb-3"
                  height="150"
                  src={profile.avatarUrl || PLACEHOLDER_COMPANY_LOGO}
                  width="150"
                />
              </div>
              <div className="col-md-6">
                <h4 className="mb-1">{profile.displayName}</h4>
                {locationLabel(profile.city, profile.district) && (
                  <p className="text-muted mb-2">
                    <i className="fas fa-map-marker-alt me-2" />
                    {locationLabel(profile.city, profile.district)}
                  </p>
                )}
                <p className="text-muted mb-3">
                  <i className="fas fa-user-clock me-2" />
                  Üye: {formatDateTr(profile.joinDate)}
                </p>
                <div className="d-flex gap-2">
                  {user?.id && user.id !== profileUserId && (
                    <Button className="btn btn-primary" onClick={handleSendMessage}>
                      <i className="fas fa-envelope me-2" />
                      Mesaj Gönder
                    </Button>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="stats text-center">
                  <div className="row g-3">
                    <div className="col-6">
                      <h5>{profile.activeListingCount}</h5>
                      <small className="text-muted">Aktif İlan</small>
                    </div>
                    <div className="col-6">
                      <h5>{ratingAverageLabel}</h5>
                      <div className="text-warning">
                        <i className="fas fa-star" />
                      </div>
                      <small className="text-muted">{profile.ratingCount} değerlendirme</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hakkında Bölümü */}
        {profile.description && profile.description.trim() !== '' && (
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
              <h5 className="card-title mb-0">
                <i className="fas fa-user me-2 text-primary" />
                Hakkında
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <p className="text-muted">{profile.description}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aktif İlanlar */}
        <h4 className="mb-3">Aktif İlanları</h4>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mb-4">
          {listings && listings.items.length > 0 ? (
            listings.items.map((listing) => (
              <AdListGridItem
                key={listing.listingId}
                ilan={sellerListingCardToAdListing(listing)}
                displayFavorites={false}
              />
            ))
          ) : (
            !listingsLoading && (
              <div className="col-12 text-center">
                <p className="text-muted">Aktif ilan bulunamadı</p>
              </div>
            )
          )}
        </div>
        {listings && listings.totalPages > 1 && (
          <div className="d-flex justify-content-center gap-2 mb-4">
            <Button
              className="btn btn-outline-primary"
              disabled={listingsPage <= 1 || listingsLoading}
              onClick={() => setListingsPage((p) => Math.max(1, p - 1))}
            >
              <i className="fas fa-chevron-left me-1" /> Önceki
            </Button>
            <span className="align-self-center text-muted">
              Sayfa {listings.page} / {listings.totalPages}
            </span>
            <Button
              className="btn btn-outline-primary"
              disabled={listingsPage >= listings.totalPages || listingsLoading}
              onClick={() => setListingsPage((p) => p + 1)}
            >
              Sonraki <i className="fas fa-chevron-right ms-1" />
            </Button>
          </div>
        )}

        {/* Değerlendirmeler */}
        <h4 className="mb-3">Değerlendirmeler</h4>
        <div className="card mb-4">
          <div className="card-body">
            {ratings && ratings.items.length > 0 ? (
              <>
                {ratings.items.map((r) => (
                  <div key={r.ratingId} className="border-bottom pb-3 mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold">{r.raterDisplayName}</span>
                      <span className="text-warning">
                        {Array.from({ length: r.stars }).map((_, i) => (
                          <i key={i} className="fas fa-star" />
                        ))}
                      </span>
                    </div>
                    {r.comment && <p className="mb-2 text-muted">{r.comment}</p>}
                    <small className="text-muted">{formatDateTr(r.createdAt)}</small>
                  </div>
                ))}
                {ratings.totalPages > 1 && (
                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <Button
                      className="btn btn-outline-primary"
                      disabled={ratingsPage <= 1 || ratingsLoading}
                      onClick={() => setRatingsPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fas fa-chevron-left me-1" /> Önceki
                    </Button>
                    <span className="align-self-center text-muted">
                      Sayfa {ratings.page} / {ratings.totalPages}
                    </span>
                    <Button
                      className="btn btn-outline-primary"
                      disabled={ratingsPage >= ratings.totalPages || ratingsLoading}
                      onClick={() => setRatingsPage((p) => p + 1)}
                    >
                      Sonraki <i className="fas fa-chevron-right ms-1" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              !ratingsLoading && <p className="text-muted text-center mb-0">Henüz değerlendirme yok</p>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SaticiProfilKurumsal;
